import OfferLabels from 'components/Common/Labels/OfferLabels'
import OfferRating from 'components/Common/NumberRating/OfferRating'
import OfferBadgeLabel from 'components/Common/OfferBadgeLabel/OfferBadgeLabel'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import BookmarkButton from 'wishlists/components/Bookmark/Buttons'
import WishlistGuard from 'wishlists/components/WishlistGuard'
import OfferTileLoggedInButtons from '../Support/OfferTileLoggedInButtons'
import { useIntl } from 'react-intl'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import styled from 'styled-components'
import Group from 'components/utils/Group'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import VillaLocationCaption from 'components/OfferList/OfferCards/VillaLocation'
import VillaCardInclusions from 'components/OfferList/OfferCards/VillaCardInclusions'
import WalledContent from 'components/Common/WalledContent'
import HeroOfferTilePricing from '../Support/HeroHotelOfferTilePricing'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import { VILLAS_REVIEWS_TOTAL_THRESHOLD } from 'constants/review'
import { hasDisplayablePackagePrice } from 'lib/offer/pricingVisibility'

const DetailContainer = styled(Group)`
  height: 100%;
  padding-top: ${rem(16)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};

  ${mediaQueryUp.desktop} {
    flex: 1;
    padding-block: ${rem(16)} ${rem(32)};
    padding-inline: ${rem(32)};
  }
`

const HeaderActions = styled.div`
  display: grid;
  grid-template: "review save";
`

const SaveButtonWrapper = styled.div`
  grid-area: save;
  justify-self: end;
`

const ReviewWrapper = styled.div`
  grid-area: review;

  &:empty {
    display: none;
  }
`

const MinHeightGroup = styled(Group)`
  min-height: ${rem(300)};
  flex-grow: 1;
`

interface Props {
  offer: App.VillaOffer | App.VillaOfferSummary
  offerUrl: string
}

function HeroVilaOfferDetails(props: Props) {
  const { offer, offerUrl } = props
  const intl = useIntl()
  const {
    property,
    lowestPricePackage,
  } = offer

  const hasPricing = hasDisplayablePackagePrice(lowestPricePackage)

  const signUpText = intl.formatMessage({
    id: 'Q9amdEZabR0g-T6FA1-9',
    defaultMessage: 'Sign up for free',
  })

  const wall = <OfferTileLoggedInButtons
    signUpText={signUpText}
    discountPercentage={lowestPricePackage?.defaultPricing?.discountPercent || 0}
    align="none"
    fullWidth
  />

  return (
    <DetailContainer direction="vertical" gap={16}>
      <CSSBreakpoint min="desktop">
        <HeaderActions>
          {isOfferRatingDisplayable(offer.property?.rating, VILLAS_REVIEWS_TOTAL_THRESHOLD) && <ReviewWrapper>
            <OfferRating variant="medium" rating={offer.property.rating} />
          </ReviewWrapper>}
          <WishlistGuard>
            <SaveButtonWrapper>
              <BookmarkButton offer={offer} />
            </SaveButtonWrapper>
          </WishlistGuard>
        </HeaderActions>
      </CSSBreakpoint>
      <MinHeightGroup direction="vertical" gap={16} tabletGap={24} verticalAlign="space-between">
        <Group direction="vertical" gap={4} horizontalAlign="start">
          <LabelGroup>
            <OfferLabels offer={offer} />
            {offer.badge && <OfferBadgeLabel badge={offer.badge} />}
          </LabelGroup>
          <Caption variant="large" colour="normal-contrast">
            <VillaLocationCaption offer={offer} />
          </Caption>
          <Heading variant="heading6" lineClamp={3}>{property?.name ?? offer.name}</Heading>
          {isOfferRatingDisplayable(offer.property?.rating, VILLAS_REVIEWS_TOTAL_THRESHOLD) && <CSSBreakpoint max="tablet">
            <OfferRating
              inlineLabel
              variant="xs"
              rating={offer.property.rating}
            />
          </CSSBreakpoint>}
          <CSSBreakpoint max="tablet">
            <VillaCardInclusions offer={offer} offerUrl={offerUrl} totalInclusions={4} />
          </CSSBreakpoint>
          <CSSBreakpoint min="desktop">
            <VillaCardInclusions offer={offer} offerUrl={offerUrl} totalInclusions={6} />
          </CSSBreakpoint>
        </Group>
        {hasPricing && <Group direction="vertical" gap={8}>
          <WalledContent enforced={offer.walledGarden} wall={wall}>
            <HeroOfferTilePricing offer={offer} pkg={lowestPricePackage} />
          </WalledContent>
        </Group>}
      </MinHeightGroup>
    </DetailContainer>
  )
}

export default HeroVilaOfferDetails
